import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Spinner } from 'react-bootstrap';
import { BarChart3, Home, Users, TrendingUp, Search } from 'lucide-react';
import axiosInstance from 'helpers/api';
import AgentMarketOverview from './components/AgentMarketOverview';
import PropertyManager from './components/PropertyManager';
import ClientMatcher from './components/ClientMatcher';
import MLSAnalytics from './components/MLSAnalytics';
import PropertySearchAgent from './components/PropertySearchAgent';

interface AgentMLSDashboardProps {
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

const AgentMLSDashboard: React.FC<AgentMLSDashboardProps> = ({
  activeTab,
  onTabChange
}) => {
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        const response = await axiosInstance.get('/api/mls-portal/agent/dashboard');
        if (response.data.success) {
          setDashboardData(response.data.data);
        } else {
          throw new Error(response.data.message || 'Failed to fetch dashboard data');
        }
      } catch (error: any) {
        console.error('Error fetching agent dashboard data:', error);
        setError(error.message || 'Failed to load dashboard');
        
        // Set mock data for development
        setDashboardData({
          activeListings: 24,
          avgDaysOnMarket: 35,
          medianPrice: 485000,
          myListings: 8,
          clientMatches: 12,
          recentActivity: [
            {
              clientName: 'Sarah Johnson',
              clientEmail: 'sarah@example.com',
              propertyType: 'Single Family',
              location: 'Phoenix, AZ',
              matchScore: 92,
              status: 'interested',
              reasons: ['Price Range Match', 'Location Preference', 'Property Type'],
              lastContact: '2 days ago'
            },
            {
              clientName: 'Mike Chen',
              clientEmail: 'mike@example.com', 
              propertyType: 'Condo',
              location: 'Scottsdale, AZ',
              matchScore: 87,
              status: 'viewing scheduled',
              reasons: ['Budget Match', 'Amenities', 'School District'],
              lastContact: '1 week ago'
            }
          ],
          priceHistory: [
            { month: 'Jan', price: 450000 },
            { month: 'Feb', price: 465000 },
            { month: 'Mar', price: 470000 },
            { month: 'Apr', price: 480000 },
            { month: 'May', price: 485000 },
            { month: 'Jun', price: 485000 }
          ],
          marketTrends: [45, 32, 28, 12]
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();

    // Set up auto-refresh every 5 minutes
    const interval = setInterval(fetchDashboardData, 300000);
    return () => clearInterval(interval);
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <AgentMarketOverview data={dashboardData} isLoading={isLoading} />;
      case 'listings':
        return <PropertyManager />;
      case 'clients':
        return <ClientMatcher />;
      case 'analytics':
        return <MLSAnalytics />;
      case 'search':
        return <PropertySearchAgent />;
      default:
        return <AgentMarketOverview data={dashboardData} isLoading={isLoading} />;
    }
  };

  // Show error state if needed
  if (error && !dashboardData) {
    return (
      <div className="agent-mls-dashboard">
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <div className="text-danger mb-3">
              <BarChart3 size={48} />
            </div>
            <h5>Unable to Load Dashboard</h5>
            <p className="text-muted mb-3">{error}</p>
            <Button 
              variant="primary" 
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="agent-mls-dashboard">
      {renderTabContent()}
    </div>
  );
};

export default AgentMLSDashboard;